import dayjs from "dayjs";
import { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import Datatable from "./Datatable";
import { useData, useDispatch } from "../../store/DataProvider";
import {
    handleApiData,
    getMonthlyExpenses,
    unsetAdminAlert,
} from "../../store/actions";
import DatepickerInput from "./DatepickerInput";
import {
    businessDate,
    asDayString,
    asMonthString,
} from "../../services/utility";

import "react-datepicker/dist/react-datepicker.css";

const MonthlyExpenses = ({
    selected,
    monthOptions,
    expenseTypeOptions,
    shopOptions,
    adminAlert,
}) => {
    const data = useData();
    const { expensesDt } = data;
    const dispatch = useDispatch();
    /* the working day, not the calendar one -- before 6am this is yesterday,
       so entering expenses late into the night defaults to the same day the
       sale screen is defaulting to */
    const today = businessDate().toISOString();

    const [monthlyExpenses, setMonthlyExpenses] = useState("");
    const [selectedMonth, setSelectedMonth] = useState("");
    const [expenseDate, setExpenseDate] = useState(today);
    const [expenseType, setExpenseType] = useState("");
    const [expenseAmount, setExpenseAmount] = useState("");
    /* Which shop anything entered here belongs to. Part of the entry, like the
       type beside it, and unset to begin with for the same reason: an expense
       has no staff member to infer a shop from the way a sale does, so the
       button asks for one rather than quietly filing everything under whichever
       shop happens to sort first. */
    const [selectedShop, setSelectedShop] = useState("");
    const [buttonMessage, setButtonMessage] = useState();
    const [buttonDisabled, setButtonDisabled] = useState(true);
    /* Which expense the form is editing, if any. The same form does both
       jobs: a second one below the list would have been the same four
       controls over again, and two places for the rules to disagree. */
    const [editing, setEditing] = useState(null);

    /* An expense that predates shops may be saved still having none: those
       cannot be attributed now with any accuracy, and demanding one before an
       unrelated correction could go in would only invite a guess. Anything
       else -- a new entry, or an expense that already has a shop -- must have
       one. Mirrored on the server, which is what actually enforces it. */
    const shopRequired = !editing || Boolean(editing.shopId);

    const getButtonMessage = () => {
        const missing = [];
        if (!expenseAmount) {
            missing.push("expense amount");
        }
        if (!expenseDate) {
            missing.push("date");
        }
        /* an expense has to say what it was for. Without this the button
           enabled with no type picked, and six expenses were saved with
           none -- the most recent two in July 2026. */
        if (!expenseType) {
            missing.push("expense type");
        }
        if (!selectedShop && shopRequired) {
            missing.push("shop");
        }
        if (missing.length) {
            /* commas between all but the last, which keeps "and" for the join
               it reads as. Two were joined by "and" alone before shops made a
               third possible, and three of them read as one long run-on. */
            const last = missing.pop();
            const list = missing.length
                ? `${missing.join(", ")} and ${last}`
                : last;
            return { disable: true, message: `Enter ${list}` };
        } else if (editing) {
            /* the date says which expense is being changed: it is the one
               thing on the row that the form shows back unaltered, so it
               still matches the highlighted line when the amount is what is
               being corrected */
            return {
                disable: false,
                message: `Save changes to ${asDayString(expenseDate)}`,
            };
        } else {
            /* the shop is named on the button. It holds from one entry to the
               next, so it can be left pointing at the shop before last, and the
               list below is the only place a wrong one shows up afterwards. */
            return {
                disable: false,
                message: `Add expense THB ${expenseAmount} to ${selectedShop.label}`,
            };
        }
    };

    useEffect(() => {
        const { value: month } = selected;
        setSelectedMonth(selected);
        getMonthlyExpenses(month, dispatch);
    }, [selected]);

    useEffect(() => {
        setMonthlyExpenses(expensesDt);
    }, [expensesDt]);

    useEffect(() => {
        const { disable, message } = getButtonMessage();
        setButtonMessage(message);
        setButtonDisabled(disable);
    }, [expenseAmount, expenseDate, expenseType, selectedShop, editing]);

    useEffect(() => {
        const { message } = adminAlert;
        if (message) {
            unsetAdminAlert(dispatch);
        }
    }, [adminAlert, dispatch]);

    const onSelectMonth = opt => {
        setSelectedMonth(opt);
        const { value: month } = opt;
        const expDate = dayjs(month).toISOString();
        /* datepicker using button needs a default date in given month */
        setExpenseDate(expDate);
        /* the expense being edited is about to leave the list, and the date
           has just been moved out from under it */
        stopEditing();
        getMonthlyExpenses(month, dispatch);
    };

    const onChangeExpense = e => {
        const value = e.target.value;
        if (value && !Number(value)) {
            return;
        }
        setExpenseAmount(value);
    };

    const onSelectExpenseType = opt => {
        setExpenseType(opt);
    };

    const dtpMinMax = [
        dayjs(selectedMonth?.value).startOf("month").valueOf(),
        dayjs(selectedMonth?.value).endOf("month").valueOf(),
    ];

    /* Loading a row into the form rather than opening a second one below the
       list: it is the same four controls, and two of them would be two places
       for the rules to disagree. The row carries the ids and the raw amount
       for this -- the displayed "฿1,234" and the type's name cannot be turned
       back into a number and an id reliably. */
    const onClickRow = row => {
        const { day, expenseTypeId, shopId, amountValue } = row;
        setEditing(row);
        setExpenseDate(dayjs(day).toISOString());
        setExpenseAmount(String(amountValue ?? ""));
        setExpenseType(
            expenseTypeOptions?.find(o => o.value === expenseTypeId) || "",
        );
        /* stays empty for the expenses that predate shops, which is what they
           are and what they may remain */
        setSelectedShop(shopOptions?.find(o => o.value === shopId) || "");
    };

    /* Back to entering. The shop is kept, as it is after a save: the next
       thing entered is usually for the same one. */
    const stopEditing = () => {
        setEditing(null);
        setExpenseAmount("");
        setExpenseType("");
    };

    const handleSubmitExpense = async e => {
        e.preventDefault();
        const data = {
            type: expenseType.label,
            /* the type itself, not just its name. The name is kept as the
               legacy record of what it was called when this was filed. */
            expenseTypeId: expenseType.value,
            expenseDate,
            /* the day as it stands in the picker, so the server files it under
               that rather than re-reading the instant in its own timezone */
            day: asDayString(expenseDate),
            shopId: selectedShop.value,
            amount: expenseAmount,
            /* absent when entering, which is what makes the same handler do
               both */
            expenseId: editing?._id,
        };
        setButtonDisabled(true);
        setExpenseAmount("");
        setExpenseType("");
        setEditing(null);
        /* the shop is left standing. Expenses are entered in batches and a
           batch is usually one shop's, so clearing it would mean picking the
           same one over and over. */
        /* awaited: the refresh below was racing the save, so a just-added
           expense could be missing from the list that came back */
        await handleApiData(data, dispatch, "saveExpense");
        const month = asMonthString(expenseDate);
        getMonthlyExpenses(month, dispatch);
    };

    /* Deleting from the form, on an expense deliberately opened, rather than
       from a double-click on the row. The double-click had to go once a
       single click started an edit -- a double-click fires a single first, so
       every delete was also loading the row into the form -- and a gesture
       that easy to trigger by accident had no business on a list of money
       records anyway. This is what Shops and Expense Types already do. */
    const handleDeleteExpense = async () => {
        const { _id, day, amount } = editing;
        if (
            !window.confirm(
                `Delete the ${amount} expense of ${day}?\n\nThis cannot be undone.`,
            )
        ) {
            return;
        }
        stopEditing();
        /* both inside the confirm now: it used to refetch the month even when
           you cancelled, and to race the delete when you did not */
        await handleApiData({ expenseId: _id }, dispatch, "deleteExpense");
        const month = asMonthString(expenseDate);
        getMonthlyExpenses(month, dispatch);
    };

    /* the row being edited, so the form and the list agree about which
       expense is in hand */
    const editingRowStyles = [
        {
            when: row => Boolean(editing) && row._id === editing._id,
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
    ];

    return (
        <>
            <div
                style={{
                    marginTop: "20px",
                    marginBottom: "9px",
                    fontSize: "22px",
                }}
            >
                Monthly Expenses
            </div>

            <div className="select-wrapper" style={{ borderRight: 0 }}>
                <Select
                    value={selectedMonth}
                    onChange={onSelectMonth}
                    options={monthOptions}
                />
            </div>

            {/* The form keeps its place and the list scrolls inside its own
                box beneath it. With the whole pane scrolling, reading down a
                month's expenses carried the form off the top of the screen --
                so the row you had just opened to edit was on screen and the
                thing editing it was not. Same arrangement as the expense
                types pane and the Sales day list. */}
            <div className="monthly-expenses-pane">
                <div style={{ marginBottom: "10px" }}>
                    <div className="spacer-div">&nbsp;</div>
                    <div
                        className="expenses-submit-container"
                        style={{
                            justifyContent: "space-between",
                            paddingTop: "10px",
                        }}
                    >
                        <div
                            className="expenses-entry expenses-entry-date"
                            style={{ paddingRight: "7px" }}
                        >
                            <DatePicker
                                selected={expenseDate}
                                onChange={date => setExpenseDate(date)}
                                dateFormat="YYYY-MM-dd"
                                placeholderText="Select date"
                                minDate={dtpMinMax[0]}
                                maxDate={dtpMinMax[1]}
                                customInput={
                                    <DatepickerInput className="datepicker-input" />
                                }
                                wrapperClassName="datepicker-wrapper"
                            />
                        </div>

                        <div
                            className="expenses-entry expenses-entry-type"
                            style={{
                                textAlign: "left",
                                paddingRight: "7px",
                            }}
                        >
                            <Select
                                options={expenseTypeOptions}
                                value={expenseType}
                                onChange={opt => onSelectExpenseType(opt)}
                                styles={{
                                    control: baseStyles => ({
                                        ...baseStyles,
                                        width: "100%",
                                        boxShadow: "0 2px 4px rgba(0,0,0, .2)",
                                        border: "0",
                                        height: "100%",
                                    }),
                                }}
                            />
                        </div>

                        <div className="expenses-entry expenses-entry-amount">
                            <input
                                className="expenses-input"
                                type="text"
                                placeholder="Enter ฿"
                                value={expenseAmount}
                                onChange={onChangeExpense}
                            />
                        </div>
                    </div>

                    {/* On its own line rather than a fourth control in the row
                        above: this column is the narrow one, and the type
                        names run long in two languages, so a four-way split
                        left neither readable. It also sits where the button
                        below names the shop back, which is the check against
                        entering a batch against the wrong one. */}
                    <div className="expenses-shop-row">
                        <Select
                            options={shopOptions}
                            value={selectedShop}
                            onChange={opt => setSelectedShop(opt)}
                            placeholder="Select shop"
                            styles={{
                                control: baseStyles => ({
                                    ...baseStyles,
                                    boxShadow: "0 2px 4px rgba(0,0,0, .2)",
                                    border: "0",
                                }),
                            }}
                        />
                    </div>

                    <div style={{ marginTop: "5px" }}>
                        <button
                            type="button"
                            className="submit-commission"
                            disabled={buttonDisabled}
                            onClick={handleSubmitExpense}
                            style={{
                                opacity: buttonDisabled ? 0.4 : 1,
                                cursor: buttonDisabled ? "default" : "cursor",
                            }}
                        >
                            {buttonMessage}
                        </button>
                    </div>

                    {/* Only while editing, so entering an expense stays the
                        one-button job it was. Delete sits apart from Cancel:
                        they are next to each other and one of them is not
                        undoable. */}
                    {editing ? (
                        <div className="expenses-edit-actions">
                            <button
                                type="button"
                                className="expenses-cancel-button"
                                onClick={stopEditing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="admin-orphan-button"
                                onClick={handleDeleteExpense}
                            >
                                Delete
                            </button>
                        </div>
                    ) : null}
                    <div
                        className="spacer-div"
                        style={{
                            borderTop: "thin solid rgba(0,0,0,.12)",
                            marginTop: "10px",
                        }}
                    >
                        &nbsp;
                    </div>
                </div>
                <div className="monthly-expenses-list">
                    {monthlyExpenses ? (
                        <Datatable
                            settings={monthlyExpenses}
                            clickFunc={onClickRow}
                            conditionalRowStyles={editingRowStyles}
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default MonthlyExpenses;
